import { ReactNode } from 'react';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { Currency } from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import { glossary } from '@sorare/core/src/lib/glossary';
import { createVar } from '@sorare/core/src/style/utils';

const numberOfDataPointsVar = createVar();

const GraphContainer = styled.div`
  --graph-padding-bottom: var(--unit);
  padding-bottom: var(--graph-padding-bottom);
`;
const Graph = styled.div`
  --left-label-width: calc(6 * var(--unit));
  --gap-label-line: var(--unit);
  --top-padding: var(--unit);
  position: relative;
  height: 200px;
`;
const Grid = styled.div`
  position: absolute;
  inset: 0;
  top: var(--top-padding);
`;
const Line = styled.div`
  width: 100%;
  position: absolute;

  &:before {
    content: '';
    width: calc(100% - var(--left-label-width) - var(--gap-label-line));
    position: absolute;
    left: calc(var(--left-label-width) + var(--gap-label-line));
    height: 1px;
    background-color: var(--c-neutral-200);
  }
`;
const Label = styled(LabelS).attrs({})`
  width: var(--left-label-width);
  text-align: right;
  position: absolute;
  top: calc(-1 * var(--unit));
`;
const Columns = styled.div`
  position: absolute;
  inset: 0;
  left: calc(
    var(--left-label-width) + var(--gap-label-line) + var(--double-unit)
  );
  top: var(--top-padding);
`;
const Column = styled.div`
  position: absolute;
  bottom: 0;
  top: 0;
  height: 100%;
`;
const PointContainer = styled.div`
  display: inline-flex;
  --point-size: 10px;
  position: absolute;
  left: calc(50% - var(--point-size) / 2);
  transform: translateY(50%);
`;
const Point = styled(Link)`
  width: var(--point-size);
  height: var(--point-size);
  display: block;
  border-radius: var(--point-size);
  background-color: var(--c-neutral-1000);
`;
const MonospaceSpan = styled.span`
  font-family: monospace;
`;

type Props = {
  data: {
    x: string;
    y: number;
    to: string;
    tooltipContent: NonNullable<ReactNode>;
  }[];
};

export const DumbGraph = ({ data }: Props) => {
  const { currency, fiatCurrency } = useCurrentUserContext();
  const { formatEth } = useIntlContext();

  const minDate = Math.min(...data.map(({ x }) => new Date(x).getTime()));
  const maxDate = new Date().getTime();

  const dateRange = maxDate - minDate;

  const min = Math.min(...data.map(({ y }) => y));
  const max = Math.max(...data.map(({ y }) => y));
  const scale = 10 ** Math.floor(Math.log10(max - min));

  const minY = Math.floor(min / scale) * scale;
  const maxY = Math.ceil(max / scale) * scale;
  const amplitude = maxY - minY;

  const lines = Array.from({ length: amplitude / scale + 1 }).map(
    (_, index) => index * scale
  );

  const commonEthDecimals = Math.max(
    ...lines
      .map((value, index, array) =>
        array.length - 1 === index
          ? 0
          : Math.floor(-Math.log10(Math.abs(value - array[index + 1])))
      )
      .filter(Boolean)
  );

  const coordinates = data.map(({ x, y }) => ({
    x: ((new Date(x).getTime() - minDate) / dateRange) * 100,
    y: ((y - minY) / amplitude) * 100,
  }));

  return (
    <GraphContainer>
      <Graph style={{ [numberOfDataPointsVar]: data.length }}>
        <Grid>
          {lines.map((value, index) => (
            <Line
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              style={{
                bottom: `${(value / amplitude) * 100}%`,
              }}
            >
              <Label as="p" color="var(--c-neutral-600)">
                {currency === Currency.FIAT ? (
                  <FormattedNumber
                    value={minY + value}
                    style="currency"
                    currency={fiatCurrency.code}
                    maximumFractionDigits={0}
                  />
                ) : (
                  <Horizontal as="span">
                    <MonospaceSpan style={{ fontFamily: 'monospace' }}>
                      {formatEth(
                        minY + value,
                        {
                          minimumFractionDigits: commonEthDecimals,
                          maximumFractionDigits: commonEthDecimals,
                        },
                        { hideSymbol: true }
                      )}
                    </MonospaceSpan>{' '}
                    <FormattedMessage {...glossary.eth} />
                  </Horizontal>
                )}
              </Label>
            </Line>
          ))}
        </Grid>
        <Columns>
          {data.map(({ to, tooltipContent }, i) => (
            <Column
              // eslint-disable-next-line react/no-array-index-key
              key={i}
              style={{
                left: `${coordinates[i].x}%`,
              }}
            >
              <PointContainer
                style={{
                  bottom: `${coordinates[i].y}%`,
                }}
              >
                <Tooltip title={tooltipContent}>
                  <Point to={to} />
                </Tooltip>
              </PointContainer>
            </Column>
          ))}
        </Columns>
      </Graph>
    </GraphContainer>
  );
};
